import { CompanyDistributor } from 'models/companies'
import { useMemo } from 'react'
import useApiService, { tagParent } from 'services/api'
import styled from 'styled-components/macro'
import { Campaign } from '../../models/campaign'
import { Deal } from '../../models/deals'
import { Tag } from '../../models/tags'
import { MULTI_VALUE_SELECT_COLORS } from '../../utils/constants'
import getStageColor from '../../utils/getStageColor'
import {
  DEFAULT_STAGE_COLOR,
  getPipelineColorForStage,
} from 'components/SalesStage/stageColors'
import { colors } from 'styles/tokens'
import { useStageColorMode } from 'components/SalesStage/StageColorContext'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { TextPill } from './TextPill'
import { RouterLink } from 'components/UI/Anchor'
import { AttributionCode } from '../../features/tradespend/tradespend_api'
import { CompanyGPO } from 'models/companies'

function ConditionalTooltipWrapper({
  shouldWrap,
  tooltipText,
  children,
}: {
  shouldWrap?: boolean
  tooltipText?: string
  children: React.ReactElement
}) {
  if (shouldWrap) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{tooltipText}</Tooltip>}
      >
        {children}
      </OverlayTrigger>
    )
  }
  return <>{children}</>
}

const defaultGrey = MULTI_VALUE_SELECT_COLORS[0].background
const defaultGreyText = MULTI_VALUE_SELECT_COLORS[0].text

export function StagePill({ stageId }: { stageId?: number | null }) {
  const api = useApiService()
  const { data } = api.useGetSalesStages()
  const colorMode = useStageColorMode()
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  if (!stageId) return null
  const stages = data ?? []
  const stage = stages.find((it) => it.id === stageId)
  if (!stage) return null

  let bg: string
  let text: string

  if (colorMode === 'pipeline') {
    const pipelineColor = getPipelineColorForStage(
      stageId,
      stages,
      customOrderEnabled
    )
    bg = pipelineColor?.bg ?? DEFAULT_STAGE_COLOR
    text = pipelineColor?.text ?? colors['fg']
  } else {
    const winRateColors = getStageColor(stage.win_rate)
    bg = winRateColors[0]
    text = winRateColors[1]
  }

  return (
    <TextPill style={{ marginBottom: 2 }} textColor={text} backgroundColor={bg}>
      {stage.stage}
    </TextPill>
  )
}

export function SaleStagesCellFromDeals({
  deals,
  sortAsc,
}: {
  deals?: Deal[] | null
  sortAsc?: boolean
}) {
  if (!deals) return <>-</>

  const stages = deals.reduce((prev, current) => {
    if (!prev.includes(current.sales_stage) && !!current.sales_stage) {
      prev.push(current.sales_stage)
    }
    return prev
  }, [] as number[])

  if (stages.length === 0) return <>-</>

  return <SalesStageCell salesStagesIds={stages} sortAsc={sortAsc} />
}

export function SalesStageCell({
  salesStagesIds,
  sortAsc,
}: {
  salesStagesIds: number[]
  sortAsc?: boolean
}) {
  const api = useApiService()
  const { data } = api.useGetSalesStages()
  const salesStagesToShow = useMemo(() => {
    const stages = data?.filter((it) => salesStagesIds.includes(it.id))
    if (sortAsc === true) {
      return stages?.sort((a, b) => a.stage.localeCompare(b.stage)) || []
    } else if (sortAsc === false) {
      return stages?.sort((a, b) => b.stage.localeCompare(a.stage)) || []
    }
    return stages || []
  }, [data, salesStagesIds])

  const salesStagesNames = useMemo(
    () => salesStagesToShow?.map((it) => it.stage),
    [salesStagesToShow]
  )
  return (
    <ConditionalTooltipWrapper
      shouldWrap={salesStagesIds.length > 2}
      tooltipText={salesStagesNames?.join(', ')}
    >
      <PillContainer data-testid="sales-stage-pill-container">
        {salesStagesToShow?.slice(0, 2).map((stage) => {
          return <StagePill key={stage.id} stageId={stage.id} />
        })}
        {salesStagesToShow.length > 2 ? (
          <span>+{salesStagesToShow.length - 2}</span>
        ) : null}
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export function ContactTagPill({
  contact_tag_id,
}: {
  contact_tag_id?: number
}) {
  const api = useApiService()
  const { data } = api.useGetTags('contacts')
  if (!contact_tag_id) return null
  const contact_tag = data?.find((it) => it.id === contact_tag_id)
  if (!contact_tag) return null
  return (
    <TextPill textColor={'#101828'} backgroundColor={'#F9F9FB'}>
      {contact_tag.tag}
    </TextPill>
  )
}

export function ContactTagCell({ deals }: { deals?: Deal[] | null }) {
  if (!deals) return <>-</>

  const tags = deals.reduce((prev, current) => {
    prev.push(...current.contact_tags)
    return prev
  }, [] as number[])

  if (tags.length === 0) return <>-</>

  return (
    <ConditionalTooltipWrapper shouldWrap={tags.length > 2}>
      <PillContainer>
        {tags?.slice(0, 2)?.map((id) => (
          <ContactTagPill key={id} contact_tag_id={id} />
        ))}
        {tags.length > 2 ? <span>+{tags.length - 2}</span> : null}
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export function ContactTagCellV2({
  contact_tags,
}: {
  contact_tags?: number[]
}) {
  const api = useApiService()
  const { data: tags } = api.useGetTags('contacts')

  if (!contact_tags) return <>-</>
  if (contact_tags.length === 0) return <>-</>

  const contactTagsToShow =
    tags?.filter((tag) => contact_tags.includes(tag.id)) ?? []
  const contactTagsNames = contactTagsToShow?.map((tag) => tag.tag)

  return (
    <ConditionalTooltipWrapper
      shouldWrap={contact_tags.length > 2}
      tooltipText={contactTagsNames?.join(', ')}
    >
      <PillContainer>
        {contact_tags?.slice(0, 2)?.map((id) => (
          <ContactTagPill key={id} contact_tag_id={id} />
        ))}
        {contact_tags.length > 2 ? (
          <span>+{contact_tags.length - 2}</span>
        ) : null}
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export function AccountOwnerPill({
  account_owner_id,
}: {
  account_owner_id?: number
}) {
  const api = useApiService()
  const { data } = api.useGetUsers()
  if (!account_owner_id) return null
  const accOwner = data?.find((it) => it.id === account_owner_id)
  if (!accOwner) return null
  const name = `${accOwner.first_name} ${accOwner.last_name}`
  return (
    <TextPill textColor={'#101828'} backgroundColor={'#F9F9FB'}>
      {name}
    </TextPill>
  )
}

export function AccountOwnerCell({
  accountOwnerIds,
  sortAsc,
}: {
  accountOwnerIds: number[]
  sortAsc?: boolean
}) {
  const api = useApiService()
  const { data } = api.useGetUsers()

  const accountOwnersToShow = useMemo(() => {
    if (!accountOwnerIds || accountOwnerIds.length === 0) return []

    const owners =
      data?.filter((user) => accountOwnerIds.includes(user.id)) || []

    if (sortAsc === true) {
      return owners.sort((a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(
          `${b.first_name} ${b.last_name}`
        )
      )
    } else if (sortAsc === false) {
      return owners.sort((a, b) =>
        `${b.first_name} ${b.last_name}`.localeCompare(
          `${a.first_name} ${a.last_name}`
        )
      )
    }
    return owners
  }, [data, accountOwnerIds, sortAsc])

  const accountOwnerNames = useMemo(
    () =>
      accountOwnersToShow.map((user) => `${user.first_name} ${user.last_name}`),
    [accountOwnersToShow]
  )

  if (accountOwnersToShow.length === 0) return <>-</>

  return (
    <ConditionalTooltipWrapper
      shouldWrap={accountOwnersToShow.length > 2}
      tooltipText={accountOwnerNames.join(', ')}
    >
      <PillContainer data-testid="account-owner-pill-container">
        {accountOwnersToShow?.slice(0, 2)?.map((user) => (
          <AccountOwnerPill key={user.id} account_owner_id={user.id} />
        ))}
        {accountOwnersToShow.length > 2 ? (
          <span>+{accountOwnersToShow.length - 2}</span>
        ) : null}
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export function TaglistCell({
  taglist,
  taglistIds,
  tagType = 'products',
  asc = true,
  maxBadges = 2,
  className,
  nullPlaceholder = <>-</>,
}: {
  taglist?: Tag[] | null
  taglistIds?: number[] | null
  tagType?: tagParent
  asc?: boolean
  maxBadges?: number
  className?: string
  nullPlaceholder?: React.JSX.Element
}) {
  const api = useApiService()
  const { data: tags } = api.useGetTags(tagType)

  const sortedTaglist = useMemo(() => {
    if (
      (!taglist || taglist.length === 0) &&
      (!taglistIds || taglistIds.length === 0)
    ) {
      return []
    }

    if (taglistIds) {
      const _taglist = tags?.filter((tag) => taglistIds.includes(tag.id)) ?? []
      if (asc) return _taglist?.sort((a, b) => a.tag.localeCompare(b.tag))
      return _taglist?.sort((a, b) => b.tag.localeCompare(a.tag))
    } else if (taglist) {
      if (asc) return taglist.sort((a, b) => a.tag.localeCompare(b.tag))
      return taglist.sort((a, b) => b.tag.localeCompare(a.tag))
    }

    return []
  }, [taglist, taglistIds, tags, asc])

  if (sortedTaglist.length === 0) return nullPlaceholder

  return (
    <ConditionalTooltipWrapper
      shouldWrap={sortedTaglist.length > maxBadges}
      tooltipText={sortedTaglist.map((tag) => tag.tag).join(', ')}
    >
      <PillContainer data-testid="taglist-pill-container" className={className}>
        {sortedTaglist?.slice(0, maxBadges)?.map((tag) => {
          const tagColor = tag.color
            ? MULTI_VALUE_SELECT_COLORS.find((c) => c.background === tag.color)
            : MULTI_VALUE_SELECT_COLORS[0]
          return (
            <TextPill
              key={tag.id}
              textColor={tagColor?.text || defaultGreyText}
              backgroundColor={tagColor?.background || defaultGrey}
            >
              {tag.tag}
            </TextPill>
          )
        })}
        {sortedTaglist.length > maxBadges ? (
          <span>+{sortedTaglist.length - maxBadges}</span>
        ) : null}
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export function DistributorsCell({
  distributors,
  distributorsIds,
  asc,
  maxBadges = 2,
  className,
  nullPlaceholder = <>-</>,
}: {
  distributors?: CompanyDistributor[] | null
  distributorsIds?: number[] | null
  asc?: boolean
  maxBadges?: number
  className?: string
  nullPlaceholder?: React.JSX.Element
}) {
  const api = useApiService()
  const { data: distributorsData } = api.useGetDistributors()

  const sortedDistributors = useMemo(() => {
    if (
      (!distributors || distributors.length === 0) &&
      (!distributorsIds || distributorsIds.length === 0)
    ) {
      return []
    }

    if (distributorsIds) {
      const _distributors =
        distributorsData?.filter((distributor) =>
          distributorsIds.includes(distributor.id)
        ) ?? []
      if (asc)
        return _distributors?.sort((a, b) => a.name.localeCompare(b.name))
      return _distributors?.sort((a, b) => b.name.localeCompare(a.name))
    } else if (distributors) {
      return distributors.sort((a, b) => {
        if (asc) return a.name.localeCompare(b.name)
        return b.name.localeCompare(a.name)
      })
    }

    return []
  }, [distributors, asc, distributorsData])

  if (sortedDistributors.length === 0) return nullPlaceholder

  return (
    <ConditionalTooltipWrapper
      shouldWrap={sortedDistributors.length > maxBadges}
      tooltipText={sortedDistributors
        .map((distributor) => distributor.name)
        .join(', ')}
    >
      <PillContainer
        data-testid="distributors-pill-container"
        className={className}
      >
        {sortedDistributors?.slice(0, maxBadges).map((distributor) => {
          return (
            <TextPill
              key={distributor.id}
              textColor={defaultGreyText}
              backgroundColor={defaultGrey}
            >
              {distributor.name}
            </TextPill>
          )
        })}
        {sortedDistributors.length > maxBadges ? (
          <span>+{sortedDistributors.length - maxBadges}</span>
        ) : null}
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export function CampaignsCell({
  campaigns,
  campaignsIds,
  asc,
}: {
  campaigns?: Campaign[] | null
  campaignsIds?: number[] | null
  asc?: boolean
}) {
  const api = useApiService()
  const { data: campaignsData } = api.useGetCampaignsOptions()

  const sorted = useMemo(() => {
    if (
      (!campaigns || campaigns.length === 0) &&
      (!campaignsIds || campaignsIds.length === 0)
    )
      return []

    if (campaignsIds) {
      if (!campaignsData) return []

      const _campaigns =
        campaignsData?.filter((co) =>
          campaignsIds.map((id) => Number(id)).includes(Number(co.value))
        ) ?? []

      if (asc) return _campaigns?.sort((a, b) => a.label.localeCompare(b.label))
      return _campaigns?.sort((a, b) => b.label.localeCompare(a.label))
    }

    if (campaigns) {
      return (
        campaigns
          .filter((campaign) => campaign != null)
          .sort((a, b) => {
            if (asc) return a.name.localeCompare(b.name)
            return b.name.localeCompare(a.name)
          })
          .map((campaign) => ({
            value: campaign.id,
            label: campaign.name,
            color: campaign.color,
          })) ?? []
      )
    }

    return []
  }, [campaigns, asc, campaignsData])

  return (
    <ConditionalTooltipWrapper
      shouldWrap={sorted.length > 2}
      tooltipText={sorted.map((campaign) => campaign.label).join(', ')}
    >
      <PillContainer>
        {sorted?.slice(0, 2).map((it) => {
          const color = it.color
            ? MULTI_VALUE_SELECT_COLORS.find((c) => c.background === it.color)
            : MULTI_VALUE_SELECT_COLORS[0]
          return (
            <TextPill
              key={it.value}
              textColor={color?.text ?? '#ffffff'}
              backgroundColor={color?.background ?? '#64d07d'}
              style={{ cursor: 'pointer' }}
            >
              <RouterLink
                to={`/campaigns/${it.value}`}
                style={{ color: color?.text ?? '#ffffff', cursor: 'pointer' }}
              >
                {it.label}
              </RouterLink>
            </TextPill>
          )
        })}
        {sorted.length > 2 ? <span>+{sorted.length - 2}</span> : null}
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export function AttributionCodeCell({
  attributionCodes,
  nullPlaceholder = <span className="text-gray-400">-</span>,
  limit = 1,
  asc = false,
  onCodeClick,
}: {
  attributionCodes?: AttributionCode[] | null
  nullPlaceholder?: React.ReactNode
  limit?: number
  asc?: boolean
  onCodeClick?: (code: AttributionCode) => void
}) {
  const sortedCodes = useMemo(() => {
    if (!attributionCodes || attributionCodes.length === 0) return []
    return attributionCodes
      .slice(0, limit)
      .sort((a, b) =>
        asc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
      )
  }, [attributionCodes, asc, limit])

  if (!attributionCodes || attributionCodes.length === 0) return nullPlaceholder

  return (
    <div className="flex flex-wrap gap-1">
      {sortedCodes.map((code) => {
        const color = code.color
          ? MULTI_VALUE_SELECT_COLORS.find((c) => c.background === code.color)
          : MULTI_VALUE_SELECT_COLORS[0]

        return (
          <TextPill
            key={code.id}
            textColor={color?.text ?? '#ffffff'}
            backgroundColor={color?.background ?? '#64d07d'}
            style={{ cursor: onCodeClick ? 'pointer' : 'default' }}
            onClick={() => onCodeClick && onCodeClick(code)}
            title={code.description || code.name}
          >
            <span>{code.name}</span>
            {code.is_default && (
              <span className="ml-1 text-xs opacity-75">(Default)</span>
            )}
          </TextPill>
        )
      })}
    </div>
  )
}

export function GPOsCell({
  gpos,
  gposIds,
  asc,
  maxBadges = 2,
  className,
  nullPlaceholder = <>-</>,
}: {
  gpos?: CompanyGPO[] | null
  gposIds?: number[] | null
  asc?: boolean
  maxBadges?: number
  className?: string
  nullPlaceholder?: React.JSX.Element
}) {
  const api = useApiService()
  const { data: gposData } = api.useGetGPOs()

  const sortedGpos = useMemo(() => {
    if ((!gpos || gpos.length === 0) && (!gposIds || gposIds.length === 0)) {
      return []
    }

    if (gposIds) {
      const _gpos = gposData?.filter((gpo) => gposIds.includes(gpo.id)) ?? []
      if (asc) return _gpos?.sort((a, b) => a.name.localeCompare(b.name))
      return _gpos?.sort((a, b) => b.name.localeCompare(a.name))
    } else if (gpos) {
      return gpos.sort((a, b) => {
        if (asc) return a.name.localeCompare(b.name)
        return b.name.localeCompare(a.name)
      })
    }

    return []
  }, [gpos, asc, gposData])

  if (sortedGpos.length === 0) return nullPlaceholder

  return (
    <ConditionalTooltipWrapper
      shouldWrap={sortedGpos.length > maxBadges}
      tooltipText={sortedGpos.map((gpo) => gpo.name).join(', ')}
    >
      <PillContainer data-testid="gpos-pill-container" className={className}>
        {sortedGpos?.slice(0, maxBadges).map((gpo) => {
          return (
            <TextPill
              key={gpo.id}
              textColor={defaultGreyText}
              backgroundColor={defaultGrey}
            >
              {gpo.name}
            </TextPill>
          )
        })}
        {sortedGpos.length > maxBadges ? (
          <span>+{sortedGpos.length - maxBadges}</span>
        ) : null}
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export const PillContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
  padding: 4px;
`

// Export TagCountBadge for convenience
export { TagCountBadge } from './TagCountBadge'
